/** One place for every user-facing date, so the app reads dd-mm-yyyy throughout
 *  instead of following each visitor's browser locale (which turned 03-08 into
 *  08/03 for anyone on a US profile). Times stay 24-hour for the same reason. */

const DATE_PARTS = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
const TIME_PARTS = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

/** Timestamps come from D1 as "YYYY-MM-DD HH:MM:SS" with no zone marker, and are
 *  always UTC, so they get a "Z" before parsing and then display in local time.
 *  A bare "YYYY-MM-DD" is a calendar day with no time zone attached: it is read
 *  as local midnight, because reading it as UTC would render the day before for
 *  anyone west of Greenwich. */
export function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  const raw = value.trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) return new Date(`${raw.replace(" ", "T")}Z`);
  return new Date(raw);
}

/** dd-mm-yyyy */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DATE_PARTS.format(date).replaceAll("/", "-");
}

/** 24-hour HH:MM */
export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "—";
  return TIME_PARTS.format(date);
}

/** dd-mm-yyyy HH:MM */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatDate(date)} ${formatTime(date)}`;
}

/** YYYY-MM-DD in local time, for populating <input type="date">. Derived from the
 *  local day so the field agrees with the date shown in lists, which can differ
 *  from the UTC day for timestamps near midnight. */
export function toDateInput(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
